import { Link } from 'react-router-dom';
import JaLayout from '@/components/feature/JaLayout';
import useSEO from '@/hooks/useSEO';

export default function JaContactInfoPage() {
  useSEO({
    title: '連絡先情報｜住所・電話・メール・LINE｜康永生技',
    description: '康永生技の連絡先：台湾台中市北区中清路一段79号6階。電話 +886-4-2202-9858。メール natural1070606@gmail.com。LINE@ @126uwznp。',
    keywords: '康永住所,康永電話,康永メール,康永LINE,サプリメント製造台湾,康永連絡先',
    lang: 'ja',
    canonicalPath: '/ja/contact/info',
  });

  return (
    <JaLayout>
      {/* Hero */}
      <section
        className="relative pt-24 pb-16 bg-cover bg-top"
        style={{ backgroundImage: "url('https://readdy.ai/api/search-image?query=natural%20green%20office%20entrance%20building%20exterior%20with%20lush%20plants%20clean%20minimalist%20architecture%20soft%20morning%20sunlight%20warm%20inviting%20biotech%20company%20facade%20nature%20inspired%20design%20serene%20environment&width=1600&height=560&seq=contact-info-hero&orientation=landscape')" }}
      >
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/50" />
        <div className="relative max-w-screen-xl mx-auto px-6 lg:px-10">
          <div className="flex items-center gap-2 mb-4 text-white/60 text-[0.8125rem]">
            <Link to="/ja" className="hover:text-white transition-colors cursor-pointer">ホーム</Link>
            <i className="ri-arrow-right-s-line" />
            <Link to="/ja/contact" className="hover:text-white transition-colors cursor-pointer">お問い合わせ</Link>
            <i className="ri-arrow-right-s-line" />
            <span className="text-white/90">連絡先情報</span>
          </div>
          <span className="inline-block bg-white/20 backdrop-blur-sm text-white text-[0.8125rem] font-medium px-4 py-1.5 rounded-full mb-4 border border-white/30">
            お問い合わせ
          </span>
          <h1 className="text-white font-bold text-[2.25rem] md:text-[3rem] leading-tight mb-4 max-w-2xl">
            連絡先情報
          </h1>
          <p className="text-white/85 text-[1.0625rem] leading-[1.8] max-w-xl">
            電話・メール・LINEにてお気軽にお問い合わせください。日本語でのご連絡を歓迎しております。
          </p>
          <div className="mt-6 h-0.5 w-14 bg-[#81C784] rounded-full" />
        </div>
      </section>

      {/* Quick CTA row */}
      <div className="bg-[#f7faf7] border-b border-gray-100">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-10 py-5">
          <div className="flex flex-col sm:flex-row flex-wrap gap-3">
            <a href="https://lin.ee/X7Hcpf4" rel="nofollow" className="whitespace-nowrap inline-flex items-center justify-center gap-2 bg-[#4CAF50] hover:bg-[#43a047] text-white font-semibold text-[0.9375rem] px-6 py-3 rounded-full transition-all duration-300 cursor-pointer w-full sm:w-auto">
              <div className="w-5 h-5 flex items-center justify-center"><i className="ri-line-fill text-base" /></div>
              LINEに追加
            </a>
            <a href="tel:+886422029858" className="whitespace-nowrap inline-flex items-center justify-center gap-2 border border-[#4CAF50] text-[#4CAF50] hover:bg-green-50 font-semibold text-[0.9375rem] px-6 py-3 rounded-full transition-all duration-300 cursor-pointer w-full sm:w-auto">
              <div className="w-5 h-5 flex items-center justify-center"><i className="ri-phone-line text-base" /></div>
              お電話で相談
            </a>
            <a href="mailto:natural1070606@gmail.com" className="whitespace-nowrap inline-flex items-center justify-center gap-2 border border-gray-300 text-[#1a2e1a] hover:border-[#4CAF50] hover:text-[#4CAF50] font-semibold text-[0.9375rem] px-6 py-3 rounded-full transition-all duration-300 cursor-pointer w-full sm:w-auto">
              <div className="w-5 h-5 flex items-center justify-center"><i className="ri-mail-line text-base" /></div>
              メールで相談
            </a>
          </div>
        </div>
      </div>

      {/* Main content */}
      <section className="py-16 bg-white">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-10">
          <div className="flex flex-col lg:flex-row gap-10 xl:gap-14">

            {/* Left: contact info cards */}
            <div className="w-full lg:flex-1 space-y-5">
              <div>
                <h2 className="text-[#1a2e1a] font-bold text-[1.5rem] mb-1.5">会社連絡先</h2>
                <p className="text-gray-500 text-[0.9375rem] leading-[1.75]">
                  Kang Yong Biotech Ltd.（康永生技有限公司） — どのような提携のご相談も歓迎いたします。
                </p>
              </div>

              {/* Company + address */}
              <div className="bg-[#f7faf7] rounded-2xl p-6 border border-gray-100">
                <div className="flex items-start gap-4">
                  <div className="w-11 h-11 flex items-center justify-center bg-green-50 rounded-xl flex-shrink-0 border border-green-100">
                    <i className="ri-building-2-line text-[#4CAF50] text-xl" />
                  </div>
                  <div>
                    <p className="text-gray-400 text-[0.75rem] mb-1">会社名・住所</p>
                    <p className="text-[#1a2e1a] font-bold text-[1.0625rem] leading-tight">Kang Yong Biotech Ltd.</p>
                    <p className="text-gray-500 text-[0.875rem] mb-2">康永生技有限公司</p>
                    <div className="flex items-start gap-2">
                      <div className="w-4 h-4 flex items-center justify-center mt-0.5 flex-shrink-0">
                        <i className="ri-map-pin-2-line text-[#4CAF50] text-sm" />
                      </div>
                      <p className="text-[#1a2e1a] font-semibold text-[0.9375rem]">
                        台湾 台中市北区中清路一段79号6階
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Phone */}
              <div className="bg-[#f7faf7] rounded-2xl p-6 border border-gray-100">
                <div className="flex items-start gap-4">
                  <div className="w-11 h-11 flex items-center justify-center bg-green-50 rounded-xl flex-shrink-0 border border-green-100">
                    <i className="ri-phone-line text-[#4CAF50] text-xl" />
                  </div>
                  <div className="flex-1">
                    <p className="text-gray-400 text-[0.75rem] mb-1">電話番号</p>
                    <a href="tel:+886422029858" className="text-[#1a2e1a] font-bold text-[1.25rem] hover:text-[#4CAF50] transition-colors cursor-pointer">
                      +886-4-2202-9858
                    </a>
                    <p className="text-gray-500 text-[0.8125rem] mt-1">月〜金 09:00〜18:00 ｜ 土 09:00〜12:00（台湾時間）</p>
                    <a href="tel:+886422029858" className="whitespace-nowrap inline-flex items-center gap-1.5 mt-3 bg-[#4CAF50] hover:bg-[#43a047] text-white font-semibold text-[0.875rem] px-5 py-2 rounded-full transition-all duration-300 cursor-pointer">
                      <div className="w-4 h-4 flex items-center justify-center"><i className="ri-phone-line text-sm" /></div>
                      今すぐ電話
                    </a>
                  </div>
                </div>
              </div>

              {/* Email */}
              <div className="bg-[#f7faf7] rounded-2xl p-6 border border-gray-100">
                <div className="flex items-start gap-4">
                  <div className="w-11 h-11 flex items-center justify-center bg-green-50 rounded-xl flex-shrink-0 border border-green-100">
                    <i className="ri-mail-line text-[#4CAF50] text-xl" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-gray-400 text-[0.75rem] mb-1">メールアドレス</p>
                    <a href="mailto:natural1070606@gmail.com" className="text-[#1a2e1a] font-bold text-[1.0625rem] hover:text-[#4CAF50] transition-colors cursor-pointer break-all">
                      natural1070606@gmail.com
                    </a>
                    <p className="text-gray-500 text-[0.8125rem] mt-1">1営業日以内にご返答いたします</p>
                    <a href="mailto:natural1070606@gmail.com" className="whitespace-nowrap inline-flex items-center gap-1.5 mt-3 border border-[#4CAF50] text-[#4CAF50] hover:bg-green-50 font-semibold text-[0.875rem] px-5 py-2 rounded-full transition-all duration-300 cursor-pointer">
                      <div className="w-4 h-4 flex items-center justify-center"><i className="ri-mail-send-line text-sm" /></div>
                      メールを送る
                    </a>
                  </div>
                </div>
              </div>

              {/* LINE */}
              <div className="bg-[#e8f5e9] border border-[#c8e6c9] rounded-2xl p-6">
                <div className="flex items-start gap-4">
                  <div className="w-11 h-11 flex items-center justify-center bg-[#4CAF50] rounded-xl flex-shrink-0">
                    <i className="ri-line-fill text-white text-xl" />
                  </div>
                  <div className="flex-1">
                    <p className="text-gray-500 text-[0.75rem] mb-1">LINE 公式アカウント</p>
                    <p className="text-[#1a2e1a] font-bold text-[1.0625rem]">@126uwznp</p>
                    <p className="text-gray-600 text-[0.875rem] mt-1 leading-[1.7]">
                      LINEでのチャット相談を歓迎しております。OEM/ODMに関するご質問、処方のご相談、製品開発のお問い合わせなど、日本語でお気軽にご連絡ください。
                    </p>
                    <a href="https://lin.ee/X7Hcpf4" rel="nofollow" className="whitespace-nowrap inline-flex items-center gap-2 mt-3 bg-[#4CAF50] hover:bg-[#43a047] text-white font-semibold text-[0.9375rem] px-6 py-2.5 rounded-full transition-all duration-300 cursor-pointer">
                      <div className="w-5 h-5 flex items-center justify-center"><i className="ri-line-fill text-base" /></div>
                      LINEに追加
                    </a>
                  </div>
                </div>
              </div>

              {/* Business hours */}
              <div className="bg-[#f7faf7] rounded-2xl p-6 border border-gray-100">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-8 h-8 flex items-center justify-center">
                    <i className="ri-time-line text-[#4CAF50] text-lg" />
                  </div>
                  <h3 className="text-[#1a2e1a] font-bold text-[1rem]">営業時間（台湾時間）</h3>
                </div>
                <div className="space-y-3">
                  {[
                    { day: '月曜日〜金曜日', time: '09:00 〜 18:00', active: true },
                    { day: '土曜日', time: '09:00 〜 12:00', active: true },
                    { day: '日曜日・祝日', time: '休業', active: false },
                  ].map((row) => (
                    <div key={row.day} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
                      <span className="text-gray-600 text-[0.9rem]">{row.day}</span>
                      <span className={`font-semibold text-[0.9rem] ${row.active ? 'text-[#1a2e1a]' : 'text-gray-400'}`}>{row.time}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Right: Google Map */}
            <div className="w-full lg:w-[500px] xl:w-[540px] flex-shrink-0 space-y-6">
              <div>
                <h2 className="text-[#1a2e1a] font-bold text-[1.5rem] mb-1.5">所在地</h2>
                <p className="text-gray-500 text-[0.9375rem] flex items-center gap-1.5">
                  <i className="ri-map-pin-line text-[#4CAF50]" />
                  台湾 台中市北区中清路一段79号6階
                </p>
              </div>

              <div className="rounded-2xl overflow-hidden border border-gray-200" style={{ minHeight: '300px' }}>
                <iframe
                  title="Kang Yong Biotech Googleマップ所在地"
                  src="https://maps.google.com/maps?q=%E5%8F%B0%E4%B8%AD%E5%B8%82%E5%8C%97%E5%8D%80%E4%B8%AD%E6%B8%85%E8%B7%AF%E4%B8%80%E6%AE%B379%E8%99%9F6%E6%A8%93&hl=ja&z=16&output=embed"
                  width="100%"
                  height="400"
                  style={{ border: 0, display: 'block' }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  className="w-full h-[300px] md:h-[400px]"
                />
              </div>

              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                <p className="text-gray-400 text-[0.8125rem] flex items-center gap-1.5 flex-1 min-w-0">
                  <i className="ri-map-pin-2-line text-[#4CAF50] flex-shrink-0" />
                  <span className="truncate">台湾 台中市北区中清路一段79号6階</span>
                </p>
                <a
                  href="https://www.google.com/maps/search/?api=1&query=%E5%8F%B0%E4%B8%AD%E5%B8%82%E5%8C%97%E5%8D%80%E4%B8%AD%E6%B8%85%E8%B7%AF%E4%B8%80%E6%AE%B379%E8%99%9F6%E6%A8%93"
                  target="_blank"
                  rel="noopener noreferrer nofollow"
                  className="whitespace-nowrap inline-flex items-center gap-1.5 bg-[#4CAF50] hover:bg-[#43a047] text-white font-semibold text-[0.875rem] px-5 py-2.5 rounded-full transition-all duration-300 cursor-pointer flex-shrink-0"
                >
                  <div className="w-4 h-4 flex items-center justify-center"><i className="ri-map-pin-line text-sm" /></div>
                  Googleマップで開く
                </a>
              </div>

              {/* アクセス方法 */}
              <div className="bg-[#f7faf7] rounded-2xl p-6 border border-gray-100">
                <h3 className="text-[#1a2e1a] font-bold text-[1rem] mb-4 flex items-center gap-2">
                  <div className="w-6 h-6 flex items-center justify-center">
                    <i className="ri-route-line text-[#4CAF50] text-base" />
                  </div>
                  アクセス方法
                </h3>
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <div className="w-7 h-7 flex items-center justify-center bg-white rounded-lg border border-gray-200 flex-shrink-0 mt-0.5">
                      <i className="ri-car-line text-[#4CAF50] text-sm" />
                    </div>
                    <div>
                      <p className="text-[#1a2e1a] font-semibold text-[0.875rem]">お車でお越しの場合</p>
                      <p className="text-gray-500 text-[0.8125rem] leading-[1.7]">中清路一段を北方向へ進み、79号のビルに到着後、エレベーターで6階までお越しください。</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-7 h-7 flex items-center justify-center bg-white rounded-lg border border-gray-200 flex-shrink-0 mt-0.5">
                      <i className="ri-bus-line text-[#4CAF50] text-sm" />
                    </div>
                    <div>
                      <p className="text-[#1a2e1a] font-semibold text-[0.875rem]">バスでお越しの場合</p>
                      <p className="text-gray-500 text-[0.8125rem] leading-[1.7]">中清路を経由するバスをご利用の上、中清路一段バス停でお降りいただき、徒歩でお越しいただけます。</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-[#1a2e1a] rounded-2xl p-6 text-white">
                <h3 className="font-bold text-[1.125rem] mb-2">OEM/ODMのご相談はこちら</h3>
                <p className="text-white/75 text-[0.875rem] leading-[1.75] mb-4">
                  お問い合わせフォームにご記入いただければ、1営業日以内に担当者よりご連絡し、最適な提携方針をご提案いたします。
                </p>
                <Link to="/ja/contact/form" className="whitespace-nowrap inline-flex items-center gap-2 bg-[#4CAF50] hover:bg-[#43a047] text-white font-semibold text-[0.9375rem] px-6 py-3 rounded-full transition-all duration-300 cursor-pointer">
                  <div className="w-5 h-5 flex items-center justify-center"><i className="ri-file-text-line text-base" /></div>
                  フォームに進む
                </Link>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* Related */}
      <section className="py-12 bg-[#f7faf7] border-t border-gray-100">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-10">
          <h3 className="text-[#1a2e1a] font-bold text-[1.125rem] mb-5">こちらもご覧ください</h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {[
              { title: 'お問い合わせフォーム', desc: 'フォームをご記入ください。1営業日以内にご返答します。', href: '/ja/contact/form' },
              { title: '提携フロー', desc: 'OEM/ODM提携の6ステップをご確認ください。', href: '/ja/cooperation/process' },
              { title: 'よくある質問', desc: 'MOQ・リードタイム・法規・輸出に関するよくある質問。', href: '/ja/cooperation/faq' },
            ].map((item) => (
              <Link key={item.href} to={item.href} className="bg-white rounded-xl p-5 border border-gray-100 hover:border-green-200 hover:-translate-y-0.5 transition-all duration-300 cursor-pointer block">
                <p className="text-[#1a2e1a] font-bold text-[0.9375rem] mb-1.5">{item.title}</p>
                <p className="text-gray-500 text-[0.875rem] leading-[1.65]">{item.desc}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </JaLayout>
  );
}
